import { useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function MutualFundsPage() {
  const [activeMode, setActiveMode] = useState<'sip' | 'lumpsum' | 'swp'>('sip');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-green-600">Mutual Funds</span>
            </h1>
            <p className="text-xl text-gray-600">
              Build wealth through professional fund management
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Video Section */}
            <section className="space-y-6">
              {/* Sachin Tendulkar - Mutual Fund Sahi Hai Video */}
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  <span className="text-green-600">Mutual Fund Sahi Hai!</span> 🏏
                </h2>
                <p className="text-gray-600 text-center mb-6">
                  Watch Sachin Tendulkar explain why mutual funds are the right choice - even when markets go up and down
                </p>
                <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/D55YAM6cjjU"
                    title="Sachin Tendulkar - Mutual Fund Sahi Hai"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
                <p className="text-sm text-gray-500 text-center mt-4 italic">
                  "Markets go up and down, but staying invested is the key to wealth creation" - The Master Blaster
                </p>
              </div>
            </section>

            {/* What are Mutual Funds */}
            <section className="bg-green-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What are Mutual Funds?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Mutual funds pool money from multiple investors to invest in stocks, bonds, and other securities. 
                Professional fund managers manage these investments, making them ideal for investors who don't have 
                time or expertise to manage individual stocks.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Think of it as hiring an expert to invest your money wisely, with the added benefit of diversification 
                across hundreds of companies.
              </p>
            </section>

            {/* Benefits */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Mutual Funds?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">📈</div>
                  <h3 className="font-bold text-lg mb-2">Higher Returns</h3>
                  <p className="text-gray-600">Historically, equity mutual funds have delivered 12-15% annual returns, beating FDs and savings accounts</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">🛡️</div>
                  <h3 className="font-bold text-lg mb-2">Professional Management</h3>
                  <p className="text-gray-600">Experienced fund managers research and manage investments on your behalf</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">🌐</div>
                  <h3 className="font-bold text-lg mb-2">Diversification</h3>
                  <p className="text-gray-600">Your investment is spread across 50-100 companies, reducing risk</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">💧</div>
                  <h3 className="font-bold text-lg mb-2">Liquidity</h3>
                  <p className="text-gray-600">Redeem your investments anytime (except ELSS lock-in period)</p>
                </div>
              </div>
            </section>

            {/* Investment Modes - SIP, Lumpsum, SWP */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Ways to Invest in Mutual Funds</h2>
              
              {/* Mode Toggle */}
              <div className="flex justify-center mb-8">
                <div className="inline-flex bg-gray-100 rounded-xl p-1 flex-wrap justify-center gap-1">
                  <button
                    onClick={() => setActiveMode('sip')}
                    className={`px-5 py-3 rounded-lg font-semibold transition-all ${
                      activeMode === 'sip'
                        ? 'bg-white text-purple-600 shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    SIP
                  </button>
                  <button
                    onClick={() => setActiveMode('lumpsum')}
                    className={`px-5 py-3 rounded-lg font-semibold transition-all ${
                      activeMode === 'lumpsum'
                        ? 'bg-white text-green-600 shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Lumpsum
                  </button>
                  <button
                    onClick={() => setActiveMode('swp')}
                    className={`px-5 py-3 rounded-lg font-semibold transition-all ${
                      activeMode === 'swp'
                        ? 'bg-white text-blue-600 shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    SWP
                  </button>
                </div>
              </div>

              {/* SIP Content */}
              {activeMode === 'sip' && (
                <div className="bg-purple-50 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-purple-600 mb-4">SIP - Systematic Investment Plan</h3>
                  <p className="text-gray-700 mb-4">
                    Invest a <strong>fixed amount regularly</strong> (monthly/quarterly) instead of a lump sum. 
                    It's like a recurring deposit, but with higher return potential.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-4 border border-purple-200">
                      <div className="text-2xl mb-2">💰</div>
                      <h4 className="font-bold mb-1">Start Small</h4>
                      <p className="text-sm text-gray-600">Begin with as little as ₹500/month</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-purple-200">
                      <div className="text-2xl mb-2">🎯</div>
                      <h4 className="font-bold mb-1">Rupee Cost Averaging</h4>
                      <p className="text-sm text-gray-600">Buy more when market is low, less when high</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-purple-200">
                      <div className="text-2xl mb-2">🤖</div>
                      <h4 className="font-bold mb-1">Automated</h4>
                      <p className="text-sm text-gray-600">Auto-debit from bank. No need to time the market</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-purple-200">
                      <div className="text-2xl mb-2">💎</div>
                      <h4 className="font-bold mb-1">Power of Compounding</h4>
                      <p className="text-sm text-gray-600">Returns generate more returns over time</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-6">
                    <h4 className="font-bold mb-3">Example: ₹10,000/month SIP @ 12%</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-sm opacity-80">10 Years</div>
                        <div className="text-xl font-bold">₹23.2L</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">20 Years</div>
                        <div className="text-xl font-bold">₹99.9L</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">30 Years</div>
                        <div className="text-xl font-bold">₹3.53Cr</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lumpsum Content */}
              {activeMode === 'lumpsum' && (
                <div className="bg-green-50 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-green-600 mb-4">Lumpsum Investment</h3>
                  <p className="text-gray-700 mb-4">
                    Invest a <strong>large amount at once</strong>. Ideal when you have surplus funds from bonus, 
                    inheritance, sale of property, or matured investments.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-4 border border-green-200">
                      <div className="text-2xl mb-2">🚀</div>
                      <h4 className="font-bold mb-1">Higher Potential Returns</h4>
                      <p className="text-sm text-gray-600">Entire amount starts compounding from day one</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-green-200">
                      <div className="text-2xl mb-2">💼</div>
                      <h4 className="font-bold mb-1">Best for Surplus Funds</h4>
                      <p className="text-sm text-gray-600">Bonus, inheritance, property sale proceeds</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-green-200">
                      <div className="text-2xl mb-2">⏱️</div>
                      <h4 className="font-bold mb-1">Market Timing Matters</h4>
                      <p className="text-sm text-gray-600">Best results when invested during market dips</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-green-200">
                      <div className="text-2xl mb-2">📈</div>
                      <h4 className="font-bold mb-1">Long-term Horizon</h4>
                      <p className="text-sm text-gray-600">Recommended for 5+ years investment period</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl p-6">
                    <h4 className="font-bold mb-3">Example: ₹10 Lakh Lumpsum @ 12%</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-sm opacity-80">5 Years</div>
                        <div className="text-xl font-bold">₹17.6L</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">10 Years</div>
                        <div className="text-xl font-bold">₹31.1L</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">15 Years</div>
                        <div className="text-xl font-bold">₹54.7L</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SWP Content */}
              {activeMode === 'swp' && (
                <div className="bg-blue-50 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-blue-600 mb-4">SWP - Systematic Withdrawal Plan</h3>
                  <p className="text-gray-700 mb-4">
                    Withdraw a <strong>fixed amount regularly</strong> from your mutual fund investment. 
                    Perfect for generating regular income during retirement or for monthly expenses.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-2xl mb-2">💵</div>
                      <h4 className="font-bold mb-1">Regular Income</h4>
                      <p className="text-sm text-gray-600">Get fixed monthly/quarterly income</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-2xl mb-2">🏖️</div>
                      <h4 className="font-bold mb-1">Retirement Planning</h4>
                      <p className="text-sm text-gray-600">Create your own pension from corpus</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-2xl mb-2">📊</div>
                      <h4 className="font-bold mb-1">Tax Efficient</h4>
                      <p className="text-sm text-gray-600">Only gains are taxed, not principal</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-2xl mb-2">🔄</div>
                      <h4 className="font-bold mb-1">Flexible</h4>
                      <p className="text-sm text-gray-600">Change withdrawal amount anytime</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-6">
                    <h4 className="font-bold mb-3">Example: ₹50 Lakh Corpus @ 10% return</h4>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-sm opacity-80">Monthly SWP</div>
                        <div className="text-xl font-bold">₹35,000/month</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">Corpus lasts</div>
                        <div className="text-xl font-bold">20+ Years</div>
                      </div>
                    </div>
                    <p className="text-sm mt-3 opacity-80">*Assuming 10% annual returns, withdrawal sustainable for 20+ years</p>
                  </div>
                </div>
              )}
            </section>

            {/* Types of Mutual Funds */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Types of Mutual Funds</h2>
              
              {/* 1. Equity Schemes */}
              <div className="mb-8">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-xl p-4">
                  <h3 className="text-xl font-bold">1. Equity Schemes</h3>
                  <p className="text-sm text-green-100 mt-1">Invest primarily in stocks - Higher growth potential for long-term wealth creation</p>
                </div>
                <div className="bg-green-50 rounded-b-xl p-6 space-y-3">
                  <div className="bg-white border-l-4 border-green-600 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 mb-1">Large Cap Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in top 100 companies by market cap. Stable and relatively lower risk.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Conservative equity investors, steady long-term growth</p>
                  </div>
                  <div className="bg-white border-l-4 border-green-600 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 mb-1">Mid Cap Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in companies ranked 101-250. Higher growth potential with moderate risk.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Investors seeking higher returns with 5-7 year horizon</p>
                  </div>
                  <div className="bg-white border-l-4 border-green-600 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 mb-1">Small Cap Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in companies ranked 251+. Highest growth potential but more volatile.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Aggressive investors with 7+ year investment horizon</p>
                  </div>
                  <div className="bg-white border-l-4 border-green-600 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 mb-1">Multi Cap Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Diversified across large, mid, and small cap stocks for balanced exposure.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Core equity portfolio, all-weather fund</p>
                  </div>
                  <div className="bg-white border-l-4 border-green-600 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 mb-1">Flexi Cap Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Flexible allocation across market caps based on opportunities.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> First-time equity investors, goal-based investing</p>
                  </div>
                  <div className="bg-white border-l-4 border-green-600 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 mb-1">Sectoral/Thematic Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Focus on specific sectors (IT, Pharma, Banking) or themes (ESG, Consumption).</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Experienced investors with sector-specific conviction</p>
                  </div>
                  <div className="bg-white border-l-4 border-orange-600 rounded-lg p-4">
                    <h4 className="font-bold text-orange-700 mb-1">ELSS (Tax Saving Funds)</h4>
                    <p className="text-sm text-gray-600 mb-2">Equity funds with 3-year lock-in. Tax deduction up to ₹1.5L under Section 80C.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Tax saving + wealth creation in one solution</p>
                  </div>
                </div>
              </div>

              {/* 2. Debt Schemes */}
              <div className="mb-8">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-xl p-4">
                  <h3 className="text-xl font-bold">2. Debt Schemes</h3>
                  <p className="text-sm text-blue-100 mt-1">Invest in fixed-income securities - Stable returns with lower risk</p>
                </div>
                <div className="bg-blue-50 rounded-b-xl p-6 space-y-3">
                  <div className="bg-white border-l-4 border-blue-600 rounded-lg p-4">
                    <h4 className="font-bold text-blue-700 mb-1">Liquid Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in very short-term instruments (up to 91 days). Park surplus cash.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Emergency funds, parking money for 1-3 months</p>
                  </div>
                  <div className="bg-white border-l-4 border-blue-600 rounded-lg p-4">
                    <h4 className="font-bold text-blue-700 mb-1">Ultra Short Duration Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in securities with 3-6 months maturity. Better than savings account.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Short-term goals (3-6 months), better FD alternative</p>
                  </div>
                  <div className="bg-white border-l-4 border-blue-600 rounded-lg p-4">
                    <h4 className="font-bold text-blue-700 mb-1">Short Duration Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in bonds maturing in 1-3 years. Moderate stability.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Goals 1-3 years away (car purchase, vacation)</p>
                  </div>
                  <div className="bg-white border-l-4 border-blue-600 rounded-lg p-4">
                    <h4 className="font-bold text-blue-700 mb-1">Medium to Long Duration Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in bonds with 3-7 year maturity. Higher returns, interest rate sensitivity.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Goals 3-5 years away, when rates expected to fall</p>
                  </div>
                  <div className="bg-white border-l-4 border-blue-600 rounded-lg p-4">
                    <h4 className="font-bold text-blue-700 mb-1">Corporate Bond Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest at least 80% in high-rated corporate bonds.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Conservative investors seeking slightly higher returns than FD</p>
                  </div>
                  <div className="bg-white border-l-4 border-blue-600 rounded-lg p-4">
                    <h4 className="font-bold text-blue-700 mb-1">Gilt Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest only in government securities. Zero credit risk.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Risk-averse investors prioritizing capital safety</p>
                  </div>
                </div>
              </div>

              {/* 3. Hybrid Schemes */}
              <div className="mb-8">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-xl p-4">
                  <h3 className="text-xl font-bold">3. Hybrid Schemes</h3>
                  <p className="text-sm text-purple-100 mt-1">Mix of equity and debt - Balanced approach for moderate risk-takers</p>
                </div>
                <div className="bg-purple-50 rounded-b-xl p-6 space-y-3">
                  <div className="bg-white border-l-4 border-purple-600 rounded-lg p-4">
                    <h4 className="font-bold text-purple-700 mb-1">Conservative Hybrid Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">10-25% equity, rest in debt. Lower risk, stable returns.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> First-time investors, retirees seeking better than FD returns</p>
                  </div>
                  <div className="bg-white border-l-4 border-purple-600 rounded-lg p-4">
                    <h4 className="font-bold text-purple-700 mb-1">Balanced Hybrid Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">40-60% equity, 40-60% debt. True balanced allocation.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Moderate risk investors, 3-5 year goals</p>
                  </div>
                  <div className="bg-white border-l-4 border-purple-600 rounded-lg p-4">
                    <h4 className="font-bold text-purple-700 mb-1">Aggressive Hybrid Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">65-80% equity, rest in debt. Higher returns with equity cushion.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Investors wanting equity returns with debt stability</p>
                  </div>
                  <div className="bg-white border-l-4 border-purple-600 rounded-lg p-4">
                    <h4 className="font-bold text-purple-700 mb-1">Dynamic Asset Allocation Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Automatically adjust equity-debt ratio based on market valuations.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Hands-off investors, automatic portfolio rebalancing</p>
                  </div>
                  <div className="bg-white border-l-4 border-purple-600 rounded-lg p-4">
                    <h4 className="font-bold text-purple-700 mb-1">Multi Asset Allocation Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest across equity, debt, and gold. True diversification.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Maximum diversification, one-fund solution</p>
                  </div>
                </div>
              </div>

              {/* 4. Other Popular Funds */}
              <div className="mb-8">
                <div className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-t-xl p-4">
                  <h3 className="text-xl font-bold">4. Other Popular Funds</h3>
                  <p className="text-sm text-yellow-100 mt-1">Specialized funds for specific investment strategies</p>
                </div>
                <div className="bg-yellow-50 rounded-b-xl p-6 space-y-3">
                  <div className="bg-white border-l-4 border-yellow-600 rounded-lg p-4">
                    <h4 className="font-bold text-yellow-700 mb-1">Index Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Passively managed funds tracking a market index like Nifty 50 or Sensex. Low cost, mirror market returns.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Long-term passive investors, low-cost core portfolio</p>
                  </div>
                  <div className="bg-white border-l-4 border-yellow-600 rounded-lg p-4">
                    <h4 className="font-bold text-yellow-700 mb-1">Gold Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in gold ETFs or physical gold. Hedge against inflation and currency risk.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Portfolio diversification, 5-10% allocation recommended</p>
                  </div>
                  <div className="bg-white border-l-4 border-yellow-600 rounded-lg p-4">
                    <h4 className="font-bold text-yellow-700 mb-1">International Funds</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in overseas markets (US, China, emerging markets). Global diversification.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Geographic diversification, exposure to global brands</p>
                  </div>
                  <div className="bg-white border-l-4 border-yellow-600 rounded-lg p-4">
                    <h4 className="font-bold text-yellow-700 mb-1">Fund of Funds (FoF)</h4>
                    <p className="text-sm text-gray-600 mb-2">Invest in other mutual fund schemes. Ready-made diversified portfolio.</p>
                    <p className="text-xs text-gray-500"><strong>Best for:</strong> Beginners wanting instant diversification in one fund</p>
                  </div>
                </div>
              </div>

              {/* Classification by Structure */}
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Classification by Structure
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-green-500 rounded-full p-2">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">Open-Ended Funds</h4>
                    </div>
                    <p className="text-gray-700 mb-4">
                      <strong>No fixed maturity period.</strong> You can buy or sell units anytime directly through the AMC website or app.
                    </p>
                    <div className="bg-green-50 rounded-lg p-4 mb-3">
                      <p className="text-sm text-gray-700"><strong>Features:</strong></p>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• High liquidity - redeem anytime</li>
                        <li>• NAV-based pricing</li>
                        <li>• No stock exchange listing needed</li>
                        <li>• Continuous buying/selling</li>
                      </ul>
                    </div>
                    <p className="text-xs text-green-700 font-semibold">✓ Most popular choice - 95% of mutual funds are open-ended</p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-blue-500 rounded-full p-2">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">Close-Ended Funds</h4>
                    </div>
                    <p className="text-gray-700 mb-4">
                      <strong>Fixed maturity period</strong> (e.g., 3, 5, 7 years). Units can be bought only during NFO (New Fund Offer). After that, traded on stock exchanges.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4 mb-3">
                      <p className="text-sm text-gray-700"><strong>Features:</strong></p>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Fixed lock-in period</li>
                        <li>• Listed on stock exchanges</li>
                        <li>• Limited liquidity</li>
                        <li>• Buy only during NFO period</li>
                      </ul>
                    </div>
                    <p className="text-xs text-blue-700 font-semibold">⚠ Best for specific investment strategies, less flexible</p>
                  </div>
                </div>
                <div className="mt-6 bg-indigo-600 text-white rounded-xl p-4 text-center">
                  <p className="text-sm">
                    <strong>💡 Expert Recommendation:</strong> For most investors, open-ended funds are the better choice due to high liquidity and flexibility to invest/redeem anytime.
                  </p>
                </div>
              </div>
            </section>

            {/* AMFI Awareness Video */}
            <section className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                <span className="text-blue-600">Smart Investing Tips</span> 💡
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Learn key principles of mutual fund investing and how to stay focused on your long-term goals
              </p>
              <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/ectXGfdol84"
                  title="AMFI Mutual Fund Awareness - Shor Ko Mute Karo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-sm text-gray-500 text-center mt-4 italic">
                Official AMFI awareness campaign to help you make informed investment decisions
              </p>
            </section>

            {/* Returns Comparison */}
            <section className="bg-gradient-to-br from-green-600 to-blue-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">Returns Comparison (Last 10 Years Average)</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white text-gray-900 rounded-xl p-6 text-center">
                  <div className="font-bold mb-1">Savings Account</div>
                  <div className="text-3xl font-black text-red-600">3-4%</div>
                </div>
                <div className="bg-white text-gray-900 rounded-xl p-6 text-center">
                  <div className="font-bold mb-1">Fixed Deposit</div>
                  <div className="text-3xl font-black text-blue-600">6-7%</div>
                </div>
                <div className="bg-white text-gray-900 rounded-xl p-6 text-center">
                  <div className="font-bold mb-1">Equity Mutual Funds</div>
                  <div className="text-3xl font-black text-green-600">12-15%</div>
                </div>
              </div>
              <p className="text-green-100 text-sm mt-4 text-center">
                *Past performance doesn't guarantee future returns. Mutual funds are subject to market risks.
              </p>
            </section>

            {/* How to Start */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Get Started</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3 font-bold">1</div>
                  <div>
                    <strong className="text-gray-900">Assess Your Goals:</strong>
                    <span className="text-gray-700"> Define what you're saving for and your time horizon</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3 font-bold">2</div>
                  <div>
                    <strong className="text-gray-900">Complete KYC:</strong>
                    <span className="text-gray-700"> One-time process with PAN, Aadhaar, bank details</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3 font-bold">3</div>
                  <div>
                    <strong className="text-gray-900">Choose Funds:</strong>
                    <span className="text-gray-700"> We'll recommend funds based on your risk profile and goals</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3 font-bold">4</div>
                  <div>
                    <strong className="text-gray-900">Start Investing:</strong>
                    <span className="text-gray-700"> Begin with SIP (as low as ₹500/month) or lump sum</span>
                  </div>
                </li>
              </ul>
            </section>

            {/* Why Choose Us */}
            <section className="bg-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Invest Through Us?</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Unbiased fund recommendations - we're not tied to any AMC</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Regular portfolio reviews and rebalancing suggestions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Goal-based financial planning - not just product selling</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Help with redemptions, switches, and tax planning</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Direct plan option available for lower expense ratios</span>
                </li>
              </ul>
            </section>

            {/* Certification Section */}
            <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-green-800 rounded-2xl p-8 text-white">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  Certified Mutual Fund Advisor
                </h2>
                <p className="text-blue-200">
                  Your investments are in safe, certified hands
                </p>
              </div>
              
              <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                {/* AMFI Logo */}
                <div className="bg-white rounded-2xl p-6 shadow-2xl">
                  <img 
                    src="/amfi-logo.png" 
                    alt="AMFI - Association of Mutual Funds in India" 
                    className="w-40 h-40 object-contain"
                  />
                </div>

                {/* Certification Details */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 max-w-md">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-500 rounded-full p-2">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold">AMFI Registered Distributor</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center border-b border-white/20 pb-2">
                      <span className="text-blue-200">ARN Number</span>
                      <span className="font-bold text-lg">349565</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/20 pb-2">
                      <span className="text-blue-200">Valid From</span>
                      <span className="font-semibold">30 Dec 2025</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/20 pb-2">
                      <span className="text-blue-200">Valid Until</span>
                      <span className="font-semibold">25 Dec 2028</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-200">Certified By</span>
                      <span className="font-semibold">AMFI India</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-sm text-green-300 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verified & Compliant with SEBI Regulations
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-center text-blue-200 text-sm mt-6">
                Association of Mutual Funds in India (AMFI) ensures that all registered distributors meet stringent compliance and ethical standards.
              </p>
            </section>

            {/* CTA */}
            <section className="text-center bg-green-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Start Building Your Wealth Today
              </h2>
              <p className="text-green-100 mb-6">
                Get personalized mutual fund recommendations from K Satish
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-green-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Request Portfolio Review
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I want to start investing in Mutual Funds"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-500 text-white px-8 py-4 rounded-xl hover:bg-blue-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* WhatsApp FAB */}
      <a
        href="https://wa.me/919248852187?text=Hi, I'm interested in learning about Mutual Funds"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:bg-green-600 hover:scale-110 transition-all duration-300 group"
        aria-label="Chat on WhatsApp about Mutual Funds"
      >
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span className="absolute bottom-full right-0 mb-2 bg-gray-900 text-white text-xs py-1 px-3 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          Chat about Mutual Funds
        </span>
      </a>

      <Footer />
    </div>
  );
}
